<template>
  <div class="content-column">
    <div class="column-header">
      <button-checkbox v-model="allItemsChecked">
        {{ name }}
      </button-checkbox>
      <span
        class="column-header-additional-buttons"
        v-if="type === 'bet'">
        <span
          class="column-header-button-simple pointer"
          @click="copyBets()">
          {{ 'general.copy' | translate }}
        </span>
        <span
          class="column-header-button-simple pointer"
          @click="pasteBets()"
          v-if="isPasteDisplayed">
          {{ 'general.paste' | translate }}
        </span>
      </span>
      <search-standard
        v-model="search"
        v-if="type !== 'sport'"
      />
    </div>
    <vue-scroll>
      <div class="column-body">
        <div
          class="column-item"
          :class="{ 'active': loadedId === item.id }"
          v-for="(item, index) in filteredItems"
          :title="item.category"
          :key="index">
          <button-checkbox
            :color="'#808b9c'"
            v-model="item.active"
            @click.native="selectAllToLeft(item); updateSelectionToRight(item);">
            <i
              class="column-item-flag flag flex-noshrink"
              :class="`flag-${item.categoryIso}`"
              v-if="item.categoryIso"
            />
            <i
              class="column-item-outright n-i n-i-podium flex-noshrink"
              v-if="item.isOutright"
            />
            {{ item.name }}
          </button-checkbox>
          <span class="filler" />
          <button-select
            class="column-item-select"
            v-model="item.betTemplate.id"
            :selections="selections"
            @select="updateSelectionToRight(item)"
            v-if="selections.length"
          />
          <span
            class="column-item-icon flex-noshrink n-i n-i-arrow-right"
            @click="loadItems(item.id)"
          />
        </div>
      </div>
    </vue-scroll>
  </div>
</template>

<script>
import ButtonCheckbox from '@/components/controls/button/ButtonCheckbox.vue';
import ButtonSelect from '@/components/controls/button/ButtonSelect.vue';
import SearchStandard from '@/components/controls/search/SearchStandard.vue';

export default {
  name: 'CustomSelectionEditColumn',
  components: {
    ButtonCheckbox,
    ButtonSelect,
    SearchStandard,
  },
  props: {
    type: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    selections: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    loadedId() {
      return this.$store.state.prematch.customSelection.selectedId[this.type] || '';
    },
    search: {
      get() {
        return this.$store.state.prematch.customSelection.search[this.type] || '';
      },
      set(val) {
        this.$store.state.prematch.customSelection.search[this.type] = val;
      },
    },
    filteredItems() {
      return this.items.filter(item => item.name.toLowerCase().includes(this.search));
    },
    allItemsChecked: {
      get() {
        return this.filteredItems.length === 0
          ? false : this.items.every(item => item.active === true);
      },
      set() {
        const checked = !this.allItemsChecked;
        this.filteredItems.forEach((item) => {
          if (item.active === checked) return;
          item.updateActiveState(checked);
          this.selectAllToLeft(item);
          this.updateSelectionToRight(item);
        });
      },
    },
    isPasteDisplayed() {
      return this.$store.state.prematch.customSelection.copiedBets.length > 0;
    },
  },
  methods: {
    loadItems(itemId) {
      this.$emit('load', itemId);
    },
    selectAllToLeft(item) {
      if (item.active) {
        if (this.type === 'sport') return;
        const sport = this.$store.getters['prematch/customSelection/sport'];
        if (sport && !sport.active) sport.updateActiveState(true);

        if (this.type === 'tournament') return;
        const tournament = this.$store.getters['prematch/customSelection/tournament'];
        if (tournament && !tournament.active) tournament.updateActiveState(true);
        if (this.type !== 'match') tournament.resetBetTemplate();

        if (this.type === 'bet') return;
        const bet = this.$store.getters['prematch/customSelection/bet'];
        if (bet && !bet.active) {
          bet.updateActiveState(true);
          tournament.resetBetTemplate();
        }
      }

      if (this.type === 'bet') {
        const tournament = this.$store.getters['prematch/customSelection/tournament'];
        tournament.resetBetTemplate();
      }
    },
    updateSelectionToRight(item) {
      if (this.type === 'match') return;
      this.$store.dispatch('prematch/customSelection/updateSelectionToRight', {
        type: this.type,
        item,
      });
    },
    copyBets() {
      this.$store.dispatch('prematch/customSelection/setCopiedBets', JSON.parse(JSON.stringify(this.items)));
    },
    pasteBets() {
      this.$store.dispatch('prematch/customSelection/pasteCopiedBets', this.items);
      const activeItem = this.items.find(item => item.active);
      if (activeItem) this.selectAllToLeft(activeItem);
    },
  },
};
</script>

<style lang="scss" scoped>
.content-column {
  position: relative;
  width: 100%;
  border-right: 1px solid #d3dadd;

  &:last-child {
    border-right: 0;
  }
}

.column-header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  height: 40px;
  color: #fff;
  background-color: rgb(95, 104, 116);
  z-index: 1;
}

.column-header-additional-buttons {
  display: flex;
  margin-right: auto;
  margin-left: 20px;
}

.column-header-button-simple {
  padding: 4px 8px;
  transition: background-color 0.4s;
  border-radius: 2px;

  &:hover {
    background-color: rgba(#000, 0.2);
  }
}

.column-body {
  height: 100%;
  padding-top: 40px;
  overflow-y: auto;
}

.column-header-checkbox {
  border-block-end: 1px solid #fff;
  margin-right: 15px;
}

.column-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 34px;
  border-bottom: 1px solid #d3dadd;

  &.active {
    background-color: #e8ecee;
  }

  /deep/ .button-checkbox-control {
    padding-left: 10px;
    width: 100%;
    height: 100%;
    justify-content: flex-end;
  }
}

.column-item-outright {
  margin-right: 6px;
}

.column-item-flag {
  margin-right: 5px;
  transform: scale(0.6);
  border-radius: 50%;
  box-shadow: 0 0 1px #000;
}

.column-item-icon {
  display: flex;
  align-items: center;
  height: 100%;
  width: 60px;
  justify-content: center;
  border-left: 1px solid #d3dadd;
  cursor: pointer;
  font-size: 0.875rem;

  &:hover {
    color: #000;
  }
}

.button-checkbox-control {
  & /deep/ span {
    display: flex;
    align-items: center;
  }
}

.column-item-select {
  margin: 0 5px;
  font-size: 0.875rem;
}
</style>
